import AxiosInstance from 'src/shared/services/axios/interceptor';
import { Invoice } from 'src/modules/invoice/models/invoice';
import { PagedResult } from 'src/shared/models/pagedResult';
import { PagingParams, setPagingParams } from 'src/shared/functions/createPagingParams';
import { InvoiceTemplate } from 'src/modules/invoice/models/invoiceTemplate';
import { InvoiceSearchFilter } from 'src/modules/invoice/models/invoiceSearchFilter';
import { InvoiceRequest } from 'src/modules/invoice/models/invoiceRequest';
import { MemberInvoiceItem } from 'src/modules/invoice/models/memberInvoiceItem';
import { MemberInvoiceSelectionSearchFilter } from 'src/modules/invoice/models/memberInvoiceSelectionSearchFilter';
import { CreateMemberInvoicesRequest } from 'src/modules/invoice/models/createMemberInvoicesRequest';
import { AxiosResponse } from 'axios';
import { MultipleInvoiceEmailSenderRequest } from 'src/modules/invoice/models/multipleInvoiceEmailSenderRequest';
import { SendSendingsBySessionIdRequest } from 'src/modules/invoice/models/sendSendingsBySessionIdRequest';
import { SendSendingsByMissionIdRequest } from 'src/modules/invoice/models/sendSendingsByMissionIdRequest';
import { InvoiceRemindersSelection } from 'src/modules/invoice/models/invoiceRemindersSelection';
import { CreateInvoiceRemindersRequest } from 'src/modules/invoice/models/createInvoiceRemindersRequest';
import { InvoiceTemplateOptions } from '../models/options/invoiceTemplateOptions';
import { InvoiceSendingTypeStringEnum } from '../models/enums/InvoiceSendingTypeString.enum';

const controller = 'Invoice';

export const getAllInvoices = (pageIndex, pageSize, sortCol, sortDir, searchFilter: InvoiceSearchFilter) => {
  let endpoint = `${controller}/?${setPagingParams({ pageIndex, pageSize, sortCol, sortDir })}`;
  endpoint += searchFilter.searchString ? `&filter=${searchFilter.searchString}` : '';
  endpoint += searchFilter.ownerId ? `&owner_id=${searchFilter.ownerId}` : '';
  endpoint += searchFilter.invoiceMissionId ? `&invoiceMission_id=${searchFilter.invoiceMissionId}` : '';
  endpoint += searchFilter.status && searchFilter.status.values[0] ? `&status=${searchFilter.status.values[0]}` : '';
  endpoint += searchFilter.sendingType ? `&sendingType=${searchFilter.sendingType}` : '';
  endpoint += searchFilter.deliveryMethod ? `&deliveryMethod=${searchFilter.deliveryMethod}` : '';
  if (searchFilter.missionType?.length) {
    searchFilter.missionType.forEach((t) => {
      endpoint += `&missionType=${encodeURIComponent(t)}`;
    });
  }
  endpoint += searchFilter.createdDateFrom ? `&createdDateFrom=${searchFilter.createdDateFrom}` : '';
  endpoint += searchFilter.createdDateTo ? `&createdDateTo=${searchFilter.createdDateTo}` : '';
  endpoint += searchFilter.paidDateFrom ? `&paidDateFrom=${searchFilter.paidDateFrom}` : '';
  endpoint += searchFilter.paidDateTo ? `&paidDateTo=${searchFilter.paidDateTo}` : '';
  endpoint += searchFilter.donor_id ? `&donor_id=${searchFilter.donor_id}` : '';
  if (searchFilter.isSent !== undefined) {
    endpoint += `&isSent=${searchFilter.isSent}`;
  }
  return AxiosInstance.get<PagedResult<Invoice>>(endpoint);
};

export const getInvoiceById = (id) => {
  const endpoint = `${controller}/${id}`;
  return AxiosInstance.get<Invoice>(endpoint);
};

export const getInvoicePdfById = (id, sendingType?: InvoiceSendingTypeStringEnum) => {
  const query = sendingType ? `/${sendingType}` : '';
  const endpoint = `${controller}/${id}/pdf${query}`;
  return AxiosInstance.get(endpoint, {
    responseType: 'blob', // VERY IMPORTANT
    headers: { Accept: 'application/pdf' },
  });
};
export const getInvoiceSendingPdfByUrl = (url) => {
  const keyword = 'InvoiceSending';
  const startIndex = url.indexOf(keyword);
  let endpoint = '';
  if (startIndex !== -1) {
    endpoint = url.slice(startIndex);
  }
  return AxiosInstance.get(endpoint, {
    responseType: 'blob', // VERY IMPORTANT
    headers: { Accept: 'application/pdf' },
  });
};

export const getInvoiceByMemberId = (pageIndex, pageSize, sortCol, sortDir, memberId) => {
  let endpoint = `${controller}/?${setPagingParams({ pageIndex, pageSize, sortCol, sortDir })}`;
  endpoint += memberId ? `&member_id=${memberId}` : '';
  return AxiosInstance.get<PagedResult<Invoice>>(endpoint);
};

export const getInvoiceTemplate = () => {
  return AxiosInstance.get<PagedResult<InvoiceTemplate>>('InvoiceTemplate');
};

export const getAllInvoiceTemplate = (opts?: InvoiceTemplateOptions) => {
  return AxiosInstance.get<PagedResult<InvoiceTemplate>>('InvoiceTemplate', { params: opts });
};
export const getInvoiceTemplateById = (templateId: number, includeSendingApprovalEmails?: boolean) => {
  let endpoint = `InvoiceTemplate/${templateId}`;
  endpoint += includeSendingApprovalEmails ? `?includeSendingApprovalEmails=${includeSendingApprovalEmails}` : '';
  return AxiosInstance.get<InvoiceTemplate>(endpoint);
};

export const updateInvoiceTemplate = (invoiceTemplate: InvoiceTemplate) => {
  return AxiosInstance.put<InvoiceTemplate>('InvoiceTemplate', invoiceTemplate);
};

export const createInvoicesTemplatePreview = (templateSendingsId: number): Promise<AxiosResponse<ArrayBuffer>> => {
  const url = `InvoiceTemplate/Preview?templateSendingsId=${templateSendingsId}`;

  return AxiosInstance.get<ArrayBuffer>(url, {
    responseType: 'arraybuffer',
  });
};

export const cancelInvoice = (id) => {
  return AxiosInstance.put(`${controller}/${id}/cancel`);
};

export const cancelInvoices = (ids: number[]) => {
  return AxiosInstance.put('/Invoice/Cancel', ids);
};

export const createInvoice = (data: InvoiceRequest) => {
  return AxiosInstance.post<InvoiceRequest>(controller, data);
};

export const getInvoiceRemindersSelection = (pageIndex, pageSize, sortCol, sortDir, data: InvoiceRemindersSelection) => {
  const endPoint = `${controller}/RemindersSelection?${setPagingParams({ pageIndex, pageSize, sortCol, sortDir })}`;

  return AxiosInstance.get<PagedResult<Invoice>>(endPoint, {
    params: data,
  });
};

export const createInvoiceReminders = (data: CreateInvoiceRemindersRequest) => {
  let endPoint = `${controller}/CreateReminders?missionId=${data.missionId}&templateId=${data.templateId}&approvalRequired=${data.approvalRequired}&sendingType=${data.sendingType}`;

  if (data.async !== undefined && data.async !== null) {
    endPoint += `&async=${data.async}`;
  }

  if (data.runAsTaskWithTaskService !== undefined && data.runAsTaskWithTaskService !== null) {
    endPoint += `&runAsTaskWithTaskService=${data.runAsTaskWithTaskService}`;
  }

  if (data.excludeNotSent === false) {
    endPoint += `&excludeNotSent=false`;
  }

  const snd = data.sndDate ?? '';

  if (data.approvalRequired && snd.length > 0) {
    endPoint += `&sndDate=${data.sndDate}`;
  }

  return AxiosInstance.put(endPoint);
};

export const GetMemberInvoiceSelection = (searchFilter: MemberInvoiceSelectionSearchFilter) => {
  interface PagedResultWithData<T> extends PagedResult<T> {
    data: {
      emailCount: number;
      mobilePhoneCount: number;
      totalCount: number;
    };
  }
  const pagingParams = {
    pageIndex: searchFilter.pageIndex,
    pageSize: searchFilter.pageSize,
    sortCol: searchFilter.sortCol,
    sortDir: searchFilter.sortDir,
  } as PagingParams;
  let queryString = `?${setPagingParams(pagingParams)}`;
  queryString += `&templateId=${searchFilter.templateId}`;
  queryString += `&filter=${searchFilter.filter}`;
  queryString += `&excludeDate=${searchFilter.excludeDate}`;
  return AxiosInstance.get<PagedResultWithData<MemberInvoiceItem>>(`/Invoice/GetMemberInvoicesSelection${queryString}`);
};

export const createMemberInvoices = (data: CreateMemberInvoicesRequest) => {
  return AxiosInstance.post<CreateMemberInvoicesRequest>(`${controller}/CreateMemberInvoices`, data);
};

export const sendSendingsBySessionId = (data: SendSendingsBySessionIdRequest) => {
  return AxiosInstance.put<SendSendingsBySessionIdRequest>(`${controller}/SendSendingsBySessionId`, data);
};

export const sendSendingsByMissionId = (data: SendSendingsByMissionIdRequest) => {
  return AxiosInstance.put<SendSendingsByMissionIdRequest>(`${controller}/SendSendingsByMissionId`, data);
};

export const createMemberInvoicesPreview = (templateId: number, payDate: string): Promise<AxiosResponse<ArrayBuffer>> => {
  const url = `MemberInvoice/CreateMemberInvoicesPreview?templateId=${templateId}&payDate=${payDate}`;
  return AxiosInstance.put<ArrayBuffer>(url, null, {
    responseType: 'arraybuffer',
  });
};

export const multipleInvoiceEmailSender = (data: MultipleInvoiceEmailSenderRequest) => {
  return AxiosInstance.put<MultipleInvoiceEmailSenderRequest>(`${controller}/Send`, data);
};

export interface InvoiceRevenueByMonth {
  yearMonth: string;
  amount: number;
  invoiceMissionId: number;
  missionName: string;
  missionType: string;
}

export interface InvoiceRevenueByType {
  membershipFee: number;
  donation: number;
  event: number;
  other: number;
  previousMembershipFee: number;
  previousDonation: number;
  previousEvent: number;
  previousOther: number;
}

export const getInvoiceRevenueByType = (startDate: string, endDate: string, prevStartDate: string, prevEndDate: string, ownerId?: number) => {
  let endpoint = `${controller}/RevenueByType?startDate=${startDate}&endDate=${endDate}&prevStartDate=${prevStartDate}&prevEndDate=${prevEndDate}`;
  if (ownerId) endpoint += `&ownerId=${ownerId}`;
  return AxiosInstance.get<InvoiceRevenueByType>(endpoint);
};

export const getInvoiceRevenueByMonth = (startDate: string, endDate: string, ownerId?: number) => {
  let endpoint = `${controller}/RevenueByMonth?startDate=${startDate}&endDate=${endDate}`;
  if (ownerId) endpoint += `&ownerId=${ownerId}`;
  return AxiosInstance.get<InvoiceRevenueByMonth[]>(endpoint);
};

export interface DeliveryMethodStats {
  deliveryMethod: string;
  totalInvoices: number;
  paidInvoices: number;
  paymentRate: number;
  avgDaysToPay: number;
}

export const getDeliveryMethodStats = (startDate: string, endDate: string, ownerId?: number, missionType?: string[], sendingType?: string) => {
  let endpoint = `${controller}/DeliveryMethodStats?startDate=${startDate}&endDate=${endDate}`;
  if (ownerId) endpoint += `&ownerId=${ownerId}`;
  if (missionType?.length) {
    missionType.forEach((t) => {
      endpoint += `&missionType=${encodeURIComponent(t)}`;
    });
  }
  if (sendingType) endpoint += `&sendingType=${encodeURIComponent(sendingType)}`;
  return AxiosInstance.get<DeliveryMethodStats[]>(endpoint);
};

export interface PaymentMethodStats {
  paymentMethod: string;
  count: number;
}

export const getPaymentMethodStats = (startDate: string, endDate: string, ownerId?: number) => {
  let endpoint = `${controller}/PaymentMethodStats?startDate=${startDate}&endDate=${endDate}`;
  if (ownerId) endpoint += `&ownerId=${ownerId}`;
  return AxiosInstance.get<PaymentMethodStats[]>(endpoint);
};
